import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Apple as AppleIcon,
  Google as GoogleIcon,
  CheckCircle as CheckIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../../store/authStore';
import { PaymentMethodService, PaymentMethod } from '../../../services/paymentMethodService';
import CompleteTransactionPopup from './CompleteTransactionPopup';
import { Payment as PaymentTestIcon } from '@mui/icons-material';
import { useTranslation } from '../../../contexts/TranslationContext';

const PaymentMethodsTab: React.FC = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(null);
  const [testPaymentDialogOpen, setTestPaymentDialogOpen] = useState(false);
  const [newMethod, setNewMethod] = useState({
    type: 'card',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    name: '',
    email: '',
  });

  // Load payment methods on component mount
  useEffect(() => {
    if (user?.id) {
      loadPaymentMethods();
    }
  }, [user?.id]);

  const loadPaymentMethods = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const methods = await PaymentMethodService.getUserPaymentMethods(user.id);
      setPaymentMethods(methods);
    } catch (error) {
      console.error('Error loading payment methods:', error);
      toast.error('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentIcon = (type: string) => {
    switch (type) {
      case 'card':
        return <CreditCardIcon />;
      case 'bank':
        return <BankIcon />;
      case 'paypal':
        return (
          <Box
            component="img"
            src="/Paypal_2014_logo.png"
            alt="PayPal"
            sx={{
              width: 40,
              height: 40,
              objectFit: 'contain',
            }}
          />
        );
      case 'apple':
        return <AppleIcon />;
      case 'google':
        return <GoogleIcon />;
      default:
        return <CreditCardIcon />;
    }
  };

  const getPaymentColor = (type: string) => {
    switch (type) {
      case 'card':
        return '#1976D2';
      case 'bank':
        return '#4CAF50';
      case 'paypal':
        return '#0070BA';
      case 'apple':
        return '#000000';
      case 'google':
        return '#4285F4';
      default:
        return '#1976D2';
    }
  };

  const handleAddMethod = async () => {
    if (!user?.id) return;

    if (newMethod.type === 'card' && (!newMethod.cardNumber || !newMethod.expiryDate || !newMethod.cvv || !newMethod.name)) {
      toast.error(t('settings.payments.toast.fillCard'));
      return;
    }

    if (newMethod.type === 'paypal' && (!newMethod.email || !newMethod.name)) {
      toast.error(t('settings.payments.toast.fillPayPal'));
      return;
    }

    try {
      const methodData = {
        user_id: user.id,
        type: newMethod.type as any,
        name: newMethod.type === 'card' ? `${newMethod.name} •••• ${newMethod.cardNumber.slice(-4)}` : newMethod.name,
        last4: newMethod.type === 'card' ? newMethod.cardNumber.slice(-4) : undefined,
        expiry: newMethod.type === 'card' ? newMethod.expiryDate : undefined,
        email: newMethod.type === 'paypal' ? newMethod.email : undefined,
        is_default: paymentMethods.length === 0,
      };

      await PaymentMethodService.addPaymentMethod(methodData);
      await loadPaymentMethods(); // Reload the list
      
      setAddDialogOpen(false);
      setNewMethod({
        type: 'card',
        cardNumber: '',
        expiryDate: '',
        cvv: '',
        name: '',
        email: '',
      });
      toast.success(t('settings.payments.toast.added'));
    } catch (error) {
      console.error('Error adding payment method:', error);
      toast.error(t('settings.payments.toast.addFailed'));
    }
  };

  const handleEditMethod = (method: PaymentMethod) => {
    setEditingMethod(method);
    setNewMethod({
      type: method.type,
      cardNumber: method.last4 ? `**** **** **** ${method.last4}` : '',
      expiryDate: method.expiry || '',
      cvv: '',
      name: method.type === 'card' ? method.name.split(' ••••')[0] : method.name,
      email: method.email || '',
    });
    setEditDialogOpen(true);
  };

  const handleUpdateMethod = async () => {
    if (!editingMethod || !user?.id) return;

    if (newMethod.type === 'card' && (!newMethod.cardNumber || !newMethod.expiryDate || !newMethod.cvv || !newMethod.name)) {
      toast.error('Please fill in all card details');
      return;
    }

    if (newMethod.type === 'paypal' && (!newMethod.email || !newMethod.name)) {
      toast.error('Please fill in PayPal email and account name');
      return;
    }

    try {
      const updates = {
        name: newMethod.type === 'card' ? `${newMethod.name} •••• ${newMethod.cardNumber.slice(-4)}` : newMethod.name,
        last4: newMethod.type === 'card' ? newMethod.cardNumber.slice(-4) : undefined,
        expiry: newMethod.type === 'card' ? newMethod.expiryDate : undefined,
        email: newMethod.type === 'paypal' ? newMethod.email : undefined,
      };

      await PaymentMethodService.updatePaymentMethod(editingMethod.id, updates);
      await loadPaymentMethods(); // Reload the list
      
      setEditDialogOpen(false);
      setEditingMethod(null);
      setNewMethod({
        type: 'card',
        cardNumber: '',
        expiryDate: '',
        cvv: '',
        name: '',
        email: '',
      });
      toast.success(t('settings.payments.toast.updated'));
    } catch (error) {
      console.error('Error updating payment method:', error);
      toast.error(t('settings.payments.toast.updateFailed'));
    }
  };

  const handleSetDefault = async (id: string) => {
    if (!user?.id) return;

    try {
      await PaymentMethodService.setDefaultPaymentMethod(id, user.id);
      await loadPaymentMethods(); // Reload the list
      toast.success(t('settings.payments.toast.defaultUpdated'));
    } catch (error) {
      console.error('Error setting default payment method:', error);
      toast.error(t('settings.payments.toast.defaultFailed'));
    }
  };

  const handleDeleteMethod = async (id: string) => {
    try {
      await PaymentMethodService.deletePaymentMethod(id);
      await loadPaymentMethods(); // Reload the list
      toast.success(t('settings.payments.toast.removed'));
    } catch (error) {
      console.error('Error deleting payment method:', error);
      toast.error(t('settings.payments.toast.removeFailed'));
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, mb: { xs: 2.5, md: 4 }, gap: { xs: 1.5, md: 2 }, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: { xs: 0.5, md: 1 }, color: 'text.primary' }}>
              {t('settings.payments.title')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: { xs: '0.95rem', md: '1rem' } }}>
              {t('settings.payments.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, auto)' }, gap: { xs: 1, md: 2 }, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<PaymentTestIcon />}
              onClick={() => setTestPaymentDialogOpen(true)}
              sx={{
                borderColor: '#4CAF50',
                color: '#4CAF50',
                '&:hover': {
                  borderColor: '#45a049',
                  backgroundColor: 'rgba(76, 175, 80, 0.08)',
                },
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                px: { xs: 2, md: 3 },
                py: { xs: 1, md: 1.25 },
              }}
            >
              {t('settings.payments.buttons.testPayment')}
            </Button>
            <Button
              variant="outlined"
              startIcon={
                <Box
                  component="img"
                  src="/Paypal_2014_logo.png"
                  alt="PayPal"
                  sx={{
                    width: 32,
                    height: 32,
                    objectFit: 'contain',
                  }}
                />
              }
              onClick={() => {
                setNewMethod(prev => ({ ...prev, type: 'paypal' }));
                setAddDialogOpen(true);
              }}
              sx={{
                borderColor: '#0070BA',
                color: '#0070BA',
                '&:hover': {
                  borderColor: '#005ea6',
                  backgroundColor: 'rgba(0, 112, 186, 0.08)',
                },
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 600,
                px: { xs: 2, md: 3 },
                py: { xs: 1, md: 1.25 },
              }}
            >
              {t('settings.payments.buttons.addPayPal')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                },
                textTransform: 'none',
                borderRadius: 2,
                px: { xs: 2, md: 3 },
                py: { xs: 1, md: 1.25 },
              }}
            >
              {t('settings.payments.buttons.addMethod')}
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* PayPal Accounts Section */}
        {paymentMethods.filter(method => method.type === 'paypal').length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              mb: 4,
              borderRadius: 3,
              border: '1px solid rgba(0, 112, 186, 0.2)',
              background: '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: '#0070BA20',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  component="img"
                  src="/Paypal_2014_logo.png"
                  alt="PayPal"
                  sx={{
                    width: 36,
                    height: 36,
                    objectFit: 'contain',
                  }}
                />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  {t('settings.payments.paypal.title')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('settings.payments.paypal.subtitle')}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: { xs: 1.5, md: 2 } }}>
              {paymentMethods
                .filter(method => method.type === 'paypal')
                .map((method, index) => (
                  <motion.div
                    key={method.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card
                      sx={{
                        borderRadius: 3,
                        border: method.is_default ? '2px solid #0070BA' : '1px solid rgba(0, 112, 186, 0.2)',
                        background: '#ffffff',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                        position: 'relative',
                        overflow: 'visible',
                      }}
                    >
                      {method.is_default && (
                        <Chip
                          label={t('settings.payments.chip.default')}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: 16,
                            backgroundColor: '#0070BA',
                            color: 'white',
                            fontWeight: 600,
                          }}
                        />
                      )}
                      
                      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              backgroundColor: '#0070BA20',
                              mr: 2,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Box
                              component="img"
                              src="/Paypal_2014_logo.png"
                              alt="PayPal"
                              sx={{
                                width: 40,
                                height: 40,
                                objectFit: 'contain',
                              }}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              {method.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {method.email}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'auto auto auto' }, gap: 1, mt: 1.5 }}>
                          {!method.is_default && (
                            <Button
                              size="small"
                              onClick={() => handleSetDefault(method.id)}
                              sx={{
                                textTransform: 'none',
                                color: '#0070BA',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 112, 186, 0.08)',
                                },
                              }}
                            >
                              {t('settings.payments.actions.setDefault')}
                            </Button>
                          )}
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditMethod(method)}
                            sx={{
                              textTransform: 'none',
                              color: 'text.secondary',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                              },
                            }}
                          >
                            {t('settings.payments.actions.edit')}
                          </Button>
                          <Button
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDeleteMethod(method.id)}
                            sx={{
                              textTransform: 'none',
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.08)',
                              },
                            }}
                          >
                            {t('settings.payments.actions.remove')}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
            </Box>
          </Paper>
        )}

        {/* Other Payment Methods */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: { xs: 1.5, md: 3 } }}>
          {paymentMethods.filter(method => method.type !== 'paypal').map((method, index) => (
            <motion.div
              key={method.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card
                sx={{
                  borderRadius: 3,
                  border: method.is_default ? '2px solid #667eea' : '1px solid rgba(0, 0, 0, 0.08)',
                  background: '#ffffff',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  position: 'relative',
                  overflow: 'visible',
                }}
              >
                {method.is_default && (
                  <Chip
                    label={t('settings.payments.chip.default')}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: 16,
                      backgroundColor: '#667eea',
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                )}
                
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: `${getPaymentColor(method.type)}20`,
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {getPaymentIcon(method.type)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {method.name}
                      </Typography>
                      {method.expiry && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t('settings.payments.expires').replace('{date}', method.expiry)}
                        </Typography>
                      )}
                      {method.email && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {method.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'auto auto auto' }, gap: 1, mt: 1.5 }}>
                    {!method.is_default && (
                      <Button
                        size="small"
                        onClick={() => handleSetDefault(method.id)}
                        sx={{
                          textTransform: 'none',
                          color: '#667eea',
                          '&:hover': {
                            backgroundColor: 'rgba(102, 126, 234, 0.08)',
                          },
                        }}
                      >
                        {t('settings.payments.actions.setDefault')}
                      </Button>
                    )}
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditMethod(method)}
                      sx={{
                        textTransform: 'none',
                        color: 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        },
                      }}
                    >
                      {t('settings.payments.actions.edit')}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteMethod(method.id)}
                      sx={{
                        textTransform: 'none',
                        color: 'error.main',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                        },
                      }}
                    >
                      {t('settings.payments.actions.remove')}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            mt: 4,
            borderRadius: 3,
            border: '1px solid rgba(0, 0, 0, 0.05)',
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(76, 175, 80, 0.02) 100%)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SecurityIcon sx={{ color: '#4CAF50', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              {t('settings.payments.security.title')}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('settings.payments.security.text')}
          </Typography>
        </Paper>
          </>
        )}
      </motion.div>

      {/* Add Payment Method Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, background: '#ffffff' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          {t('settings.payments.addDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              fullWidth
              label={t('settings.payments.addDialog.typeLabel')}
              value={newMethod.type}
              onChange={(e) => setNewMethod(prev => ({ ...prev, type: e.target.value as any }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <option value="card">{t('settings.payments.type.card')}</option>
              <option value="paypal">{t('settings.payments.type.paypal')}</option>
            </TextField>

            {newMethod.type === 'card' ? (
              <>
                <TextField
                  fullWidth
                  label={t('settings.payments.fields.cardNumber')}
                  value={newMethod.cardNumber}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, cardNumber: e.target.value }))}
                  placeholder="1234 5678 9012 3456"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label={t('settings.payments.fields.expiryDate')}
                    value={newMethod.expiryDate}
                    onChange={(e) => setNewMethod(prev => ({ ...prev, expiryDate: e.target.value }))}
                    placeholder="MM/YY"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    fullWidth
                    label={t('settings.payments.fields.cvv')}
                    value={newMethod.cvv}
                    onChange={(e) => setNewMethod(prev => ({ ...prev, cvv: e.target.value }))}
                    placeholder="123"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label={t('settings.payments.fields.cardholderName')}
                  value={newMethod.name}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label={t('settings.payments.fields.paypalEmail')}
                  type="email"
                  value={newMethod.email}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your.email@example.com"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label={t('settings.payments.fields.accountName')}
                  value={newMethod.name}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="My PayPal Account"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setAddDialogOpen(false)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {t('settings.common.cancel')}
          </Button>
          <Button
            onClick={handleAddMethod}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              textTransform: 'none',
              borderRadius: 2,
            }}
          >
            {t('settings.payments.addDialog.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Payment Method Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          {t('settings.payments.editDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              fullWidth
              label={t('settings.payments.addDialog.typeLabel')}
              value={newMethod.type}
              onChange={(e) => setNewMethod(prev => ({ ...prev, type: e.target.value as any }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <option value="card">{t('settings.payments.type.card')}</option>
              <option value="paypal">{t('settings.payments.type.paypal')}</option>
            </TextField>

            {newMethod.type === 'card' ? (
              <>
                <TextField
                  fullWidth
                  label={t('settings.payments.fields.cardNumber')}
                  value={newMethod.cardNumber}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, cardNumber: e.target.value }))}
                  placeholder="1234 5678 9012 3456"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label={t('settings.payments.fields.expiryDate')}
                    value={newMethod.expiryDate}
                    onChange={(e) => setNewMethod(prev => ({ ...prev, expiryDate: e.target.value }))}
                    placeholder="MM/YY"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    fullWidth
                    label={t('settings.payments.fields.cvv')}
                    value={newMethod.cvv}
                    onChange={(e) => setNewMethod(prev => ({ ...prev, cvv: e.target.value }))}
                    placeholder="123"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label={t('settings.payments.fields.cardholderName')}
                  value={newMethod.name}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label={t('settings.payments.fields.paypalEmail')}
                  type="email"
                  value={newMethod.email}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your.email@example.com"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label={t('settings.payments.fields.accountName')}
                  value={newMethod.name}
                  onChange={(e) => setNewMethod(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="My PayPal Account"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {t('settings.common.cancel')}
          </Button>
          <Button
            onClick={handleUpdateMethod}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              textTransform: 'none',
              borderRadius: 2,
            }}
          >
            {t('settings.payments.editDialog.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Payment Dialog */}
      <CompleteTransactionPopup
        open={testPaymentDialogOpen}
        onClose={() => setTestPaymentDialogOpen(false)}
      />
    </Box>
  );
};

export default PaymentMethodsTab;
